import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useAuthUserRestaurant,
  useGetRestaurantCompaniesQuery,
} from '@durma-soft/gros-sdk';

import { useTableSearchParams } from '@/hooks/use-table-search-params';

import { Link } from '@/components/shared/link';
import { SearchInput } from '@/components/shared/search-input';
import { DataTable } from '@/components/shared/data-table';
import { buttonVariants } from '@/components/shared/shadcn-ui/button';
import { EditRestaurantCompanyModal } from '@/components/restaurant/companies/edit-restaurant-company-modal';
import { CreateRestaurantCompanyModal } from '@/components/restaurant/companies/create-restaurant-company-modal';

import { columns } from '@/config/restaurant/companies';
import { cn } from '@/utils/helpers';

const RestaurantCompanies = () => {
  const { t } = useTranslation();

  const {
    pagination,
    setPagination,
    sorting,
    setSorting,
    searchInput,
    debouncedSearchInput,
    handleSearchChange,
  } = useTableSearchParams();

  const { companyId } = useParams();
  const memoizedColumns = useMemo(() => {
    return columns(t);
  }, [t]);

  const restaurant = useAuthUserRestaurant();

  const { data, isLoading, isPlaceholderData } = useGetRestaurantCompaniesQuery(
    {
      restaurant_id: restaurant.id,
      q: debouncedSearchInput,
      page: pagination.pageIndex + 1,
      page_size: pagination.pageSize,
      sort_by: sorting[0]?.id,
      sort_order: sorting[0]?.desc ? 'desc' : 'asc',
    },
  );

  return (
    <>
      <Helmet>
        <title>{t('company.title')}</title>
      </Helmet>
      <div>
        <div className="flex items-center">
          <h1 className="text-lg font-semibold md:text-2xl">
            {t('company.title')}
          </h1>
        </div>
        <div className="flex flex-col gap-3">
          <div className="self-end">
            <Link
              to="/restaurant/companies/create"
              className={buttonVariants({ variant: 'default' })}
            >
              {t('company.create')}
            </Link>
          </div>
          <div
            className={cn(
              'flex flex-col gap-3',
              isPlaceholderData && 'opacity-50',
            )}
          >
            <SearchInput
              className="self-start p-0"
              placeholder={t('common.search-placeholder')}
              searchInput={searchInput}
              handleChange={handleSearchChange}
            />
            <DataTable
              serverSideSorting
              columns={memoizedColumns}
              data={data?.data}
              pageCount={data?.total_pages}
              isLoading={isLoading}
              sorting={sorting}
              setSorting={setSorting}
              pagination={pagination}
              setPagination={setPagination}
            />
          </div>
        </div>
      </div>
      <CreateRestaurantCompanyModal />
      {Boolean(companyId) && <EditRestaurantCompanyModal key={companyId} />}
    </>
  );
};

export default RestaurantCompanies;
